import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import DateInputBR from "@/components/DateInputBR";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Clock, RefreshCw, AlertCircle, Upload, FileDown, Plus, History, Trash2,
  CheckCircle2, XCircle, Info, Settings2,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import { PageHero, KpiCard, Section, GridSkeleton, EmptyState } from "@/components/AnalyticsCommon";
import { fmtDate, fmtDateTime } from "@/lib/format";
import GlobalDashboardFilters, { DEFAULT_FILTERS, filtersToParams } from "@/components/GlobalDashboardFilters";
import {
  ResponsiveContainer as RC2, LineChart, Line, ComposedChart,
} from "recharts";
import { ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";

function DeltaBadge({ value, suffix = "%", invert = true }) {
  const isZero = !value || Math.abs(value) < 0.01;
  const positive = value > 0;
  const good = invert ? !positive : positive;
  const color = isZero ? "text-slate-500 bg-slate-100"
    : good ? "text-emerald-700 bg-emerald-50" : "text-rose-700 bg-rose-50";
  const Icon = isZero ? Minus : positive ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] font-semibold ${color}`}>
      <Icon className="h-3 w-3" />
      {value > 0 ? "+" : ""}{value}{suffix}
    </span>
  );
}

function YoyChart({ yoy }) {
  const byMonth = {};
  yoy.forEach((r) => {
    byMonth[r.month_num] = byMonth[r.month_num] || { month_num: r.month_num, month_label: r.month.split("/")[0] };
    byMonth[r.month_num][`y${r.year}`] = r.pct;
  });
  const rows = Object.values(byMonth).sort((a, b) => a.month_num - b.month_num);
  const years = [...new Set(yoy.map((r) => r.year))].sort();
  return (
    <div className="h-[280px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={rows} margin={{ top: 5, right: 8, bottom: 5, left: -8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DD" vertical={false} />
          <XAxis dataKey="month_label" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} unit="%" />
          <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
          {years.map((yr, i) => (
            <Line key={yr} type="monotone" dataKey={`y${yr}`}
                  stroke={i === 0 ? "#94A3B8" : "#234935"}
                  strokeWidth={i === 0 ? 1.8 : 2.5}
                  strokeDasharray={i === 0 ? "5 4" : ""}
                  name={`${yr}`} dot={{ r: 3 }} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

const TIPO_LABELS = {
  falta_injustificada: "Falta injustificada",
  falta_justificada: "Falta justificada",
  atestado_medico: "Atestado médico",
  afastamento_inss: "Afastamento INSS",
  licenca_maternidade: "Licença maternidade",
  licenca_paternidade: "Licença paternidade",
  acidente_trabalho: "Acidente de trabalho",
  suspensao: "Suspensão",
  atraso: "Atraso",
  saida_antecipada: "Saída antecipada",
  falta_parcial: "Falta parcial",
  outros: "Outros",
};

export default function AbsenteismoPage() {
  const { companyParam, companies, selected, select } = useCompany();
  const { hasRole } = useAuth();
  const canEdit = hasRole("rh_admin", "rh_operacional");
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("painel");

  // List tab
  const [list, setList] = useState({ items: [], total: 0 });
  const [listLoading, setListLoading] = useState(false);
  const [filters, setFilters] = useState({ tipo: "all", origem: "all", matricula: "", date_from: "", date_to: "" });

  // Imports tab
  const [imports, setImports] = useState([]);

  // Global filters (Month/Year/Company/Sector/Supervisor/View Mode)
  const [gfilters, setGfilters] = useState(DEFAULT_FILTERS);

  // Import dialog state
  const [importOpen, setImportOpen] = useState(false);
  const [importStep, setImportStep] = useState(1); // 1=upload, 2=preview, 3=done
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const fileInputRef = useRef(null);

  // Manual dialog state
  const [manualOpen, setManualOpen] = useState(false);
  const emptyManual = {
    matricula: "", data_ausencia: new Date().toISOString().slice(0, 10),
    tipo_ausencia: "falta_injustificada", motivo_ausencia: "",
    horas_ausentes: 0, dias_ausentes: 1, justificativa: "injustificada",
    possui_atestado: false, cid: "", observacao: "", supervisor: "",
  };
  const [manualForm, setManualForm] = useState(emptyManual);

  const loadKpis = async () => {
    setLoading(true);
    try {
      const params = filtersToParams(gfilters);
      if (companyParam && !params.company_id) params.company_id = companyParam;
      const { data: d } = await api.get("/performance/absenteismo", { params });
      setKpis(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const loadList = async () => {
    setListLoading(true);
    try {
      const params = { page_size: 200 };
      if (companyParam) params.company_id = companyParam;
      if (filters.tipo !== "all") params.tipo = filters.tipo;
      if (filters.origem !== "all") params.origem = filters.origem;
      if (filters.matricula.trim()) params.matricula = filters.matricula.trim();
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const { data } = await api.get("/absenteismo", { params });
      setList(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setListLoading(false); }
  };

  const loadImports = async () => {
    try {
      const { data } = await api.get("/absenteismo/imports");
      setImports(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadKpis(); }, [companyParam, JSON.stringify(gfilters)]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (tab === "registros") loadList();
    if (tab === "historico") loadImports();
  }, [tab, companyParam]);

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/absenteismo/template", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "modelo_absenteismo.xlsx"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Modelo baixado");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportXlsx = async () => {
    try {
      const params = {};
      if (companyParam) params.company_id = companyParam;
      if (filters.tipo !== "all") params.tipo = filters.tipo;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const r = await api.get("/absenteismo/export.xlsx", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `absenteismo-${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel exportado");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ---- Import flow ----
  const resetImport = () => {
    setImportStep(1); setImportFile(null); setImportPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const doPreview = async () => {
    if (!importFile) { toast.error("Selecione um arquivo"); return; }
    setImportBusy(true);
    try {
      const fd = new FormData(); fd.append("file", importFile);
      const { data } = await api.post("/absenteismo/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportPreview(data); setImportStep(2);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setImportBusy(false); }
  };

  const doCommit = async () => {
    if (!importPreview?.valid?.length) { toast.error("Nenhuma linha válida"); return; }
    setImportBusy(true);
    try {
      const { data } = await api.post("/absenteismo/import/commit", {
        filename: importPreview.filename,
        rows: importPreview.valid,
      });
      toast.success(`${data.inserted} registro(s) importado(s)`);
      setImportStep(3);
      loadKpis();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setImportBusy(false); }
  };

  const revertImport = async (imp) => {
    if (!window.confirm(`Reverter importação "${imp.filename}" de ${fmtDateTime(imp.created_at)}?\nIsso excluirá ${imp.row_count} registro(s).`)) return;
    try {
      const { data } = await api.post(`/absenteismo/imports/${imp.id}/revert`);
      toast.success(`Importação revertida — ${data.deleted} registro(s) removido(s)`);
      loadImports(); loadKpis();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveManual = async () => {
    try {
      if (!manualForm.matricula.trim() || !manualForm.data_ausencia) {
        toast.error("Matrícula e data são obrigatórias");
        return;
      }
      await api.post("/absenteismo", manualForm);
      toast.success("Ausência registrada");
      setManualOpen(false);
      setManualForm(emptyManual);
      loadKpis();
      if (tab === "registros") loadList();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteAbsence = async (a) => {
    if (!window.confirm(`Excluir ausência de ${a.nome_colaborador || a.matricula} em ${fmtDate(a.data_ausencia)}?`)) return;
    try {
      await api.delete(`/absenteismo/${a.id}`);
      toast.success("Ausência excluída");
      loadList(); loadKpis();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const k = kpis?.kpis;
  const empty = kpis?.data_status === "vazio";

  return (
    <div className="space-y-5 pb-10" data-testid="absent-page">
      <PageHero
        kicker={<><Clock className="inline h-3 w-3 mr-1.5 -mt-0.5" /> Performance & Analytics</>}
        title="Indicadores de Absenteísmo"
        description="Análise de faltas, atestados, atrasos e afastamentos. Fórmula: horas perdidas ÷ horas previstas × 100."
        actions={
          <>
            <Select value={selected || "all"} onValueChange={select}>
              <SelectTrigger className="h-10 w-full sm:w-[220px] border-white/20 bg-white/10 text-white" data-testid="abs-company">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas (Grupo)</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</SelectItem>)}
              </SelectContent>
            </Select>
            {canEdit && (
              <Button size="sm" variant="outline" onClick={() => { resetImport(); setImportOpen(true); }}
                      className="h-10 border-white/20 bg-white/5 text-white hover:bg-white/15"
                      data-testid="abs-import-btn">
                <Upload className="h-3.5 w-3.5 mr-1.5" /> Importar
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={downloadTemplate}
                    className="h-10 border-white/20 bg-white/5 text-white hover:bg-white/15"
                    data-testid="abs-template-btn">
              <FileDown className="h-3.5 w-3.5 mr-1.5" /> Modelo Excel
            </Button>
            {canEdit && (
              <Button size="sm" onClick={() => { setManualForm(emptyManual); setManualOpen(true); }}
                      className="h-10 bg-[#C5A059] hover:bg-[#D4B36B] text-[#1A231F]"
                      data-testid="abs-manual-btn">
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Lançamento manual
              </Button>
            )}
          </>
        }
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="painel" data-testid="abs-tab-painel">Painel Gerencial</TabsTrigger>
          <TabsTrigger value="indicadores" data-testid="abs-tab-indicadores">Indicadores Básicos</TabsTrigger>
          <TabsTrigger value="registros" data-testid="abs-tab-registros">Registros</TabsTrigger>
          <TabsTrigger value="historico" data-testid="abs-tab-historico">Histórico de importações</TabsTrigger>
        </TabsList>

        <TabsContent value="painel" className="space-y-4 mt-4">
          <AdvancedPanel companyParam={companyParam} />
        </TabsContent>

        {/* ---- Indicadores ---- */}
        <TabsContent value="indicadores" className="space-y-4 mt-4">
          <GlobalDashboardFilters
            value={gfilters}
            onChange={setGfilters}
            companies={companies}
            testIdPrefix="abs-filters"
          />
          {loading ? <GridSkeleton /> : empty ? (
            <EmptyState message={kpis.message} schema={kpis.schema} />
          ) : k ? (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="abs-kpis">
                <KpiCard label={`Absenteísmo · ${kpis.period?.label || "—"}`} value={`${k.absenteismo_pct}%`}
                        sub={`${k.horas_perdidas}h de ${k.horas_previstas}h previstas`} icon={Clock}
                        accent={k.absenteismo_pct < 2 ? "emerald" : k.absenteismo_pct < 5 ? "ochre" : "rose"} />
                <KpiCard label="Horas perdidas" value={k.horas_perdidas} icon={AlertCircle} accent="rose" />
                <KpiCard label="Ocorrências" value={k.total_ocorrencias} icon={AlertCircle} accent="ochre" />
                <KpiCard label="Atestados" value={k.atestados} icon={Clock} accent="sky" />
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <KpiCard label="Faltas justificadas" value={k.faltas_justificadas} accent="emerald" />
                <KpiCard label="Faltas injustificadas" value={k.faltas_injustificadas} accent="rose" />
                <KpiCard label="Atrasos" value={k.atrasos} accent="ochre" />
                <KpiCard label="Saídas antecipadas" value={k.saidas_antecipadas} accent="slate" />
              </div>

              {kpis.comparative && (
                <Section kicker="Comparativo" title={`${kpis.comparative.current.label} × ${kpis.comparative.previous.label}`}
                         testId="abs-comparative">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="rounded-lg border border-border bg-emerald-50/40 p-3">
                      <div className="text-[10.5px] uppercase tracking-wide text-emerald-800 font-semibold mb-1">Atual</div>
                      <div className="text-2xl font-bold text-[#234935] tabular-nums">{kpis.comparative.current.pct}%</div>
                      <div className="text-[11px] text-muted-foreground mt-1">
                        {kpis.comparative.current.horas_perdidas}h perdidas
                      </div>
                    </div>
                    <div className="rounded-lg border border-border bg-slate-50/60 p-3">
                      <div className="text-[10.5px] uppercase tracking-wide text-slate-700 font-semibold mb-1">{kpis.comparative.previous.label}</div>
                      <div className="text-2xl font-bold text-slate-700 tabular-nums">{kpis.comparative.previous.pct}%</div>
                      <div className="text-[11px] text-muted-foreground mt-1">
                        {kpis.comparative.previous.horas_perdidas}h perdidas
                      </div>
                    </div>
                    <div className="rounded-lg border border-border bg-white p-3">
                      <div className="text-[10.5px] uppercase tracking-wide text-muted-foreground mb-1">Variação</div>
                      <DeltaBadge value={kpis.comparative.delta_pct} suffix="%" invert />
                    </div>
                  </div>
                </Section>
              )}

              {kpis.evolution?.length > 0 && (
                <Section kicker="Tendência" title="Evolução mensal · 12 meses" testId="abs-evolution">
                  <div className="h-[280px]">
                    <RC2 width="100%" height="100%">
                      <ComposedChart data={kpis.evolution} margin={{ top: 5, right: 8, bottom: 5, left: -8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DD" vertical={false} />
                        <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                        <YAxis yAxisId="l" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                        <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} unit="%" />
                        <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                        <Bar yAxisId="l" dataKey="horas_perdidas" fill="#C5A059" name="Horas perdidas" radius={[4, 4, 0, 0]} />
                        <Line yAxisId="r" type="monotone" dataKey="pct" stroke="#234935" strokeWidth={2.5} name="Absenteísmo %" dot={{ r: 3 }} />
                      </ComposedChart>
                    </RC2>
                  </div>
                </Section>
              )}

              {kpis.yoy_series?.length > 0 && (
                <Section kicker="Year over Year" title="Absenteísmo % por mês · entre anos" testId="abs-yoy">
                  <YoyChart yoy={kpis.yoy_series} />
                </Section>
              )}

              {kpis.by_sector?.length > 0 && (
                <Section kicker="Por setor" title="Absenteísmo por setor">
                  <div className="h-[320px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={kpis.by_sector.slice(0, 12)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DD" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11, fill: "#6B7280" }} unit="%" />
                        <YAxis type="category" dataKey="sector" tick={{ fontSize: 11, fill: "#374151" }} axisLine={false} tickLine={false} width={130} />
                        <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                        <Bar dataKey="pct" fill="#C5A059" name="Absenteísmo %" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Section>
              )}

              {kpis.ranking_colaboradores?.length > 0 && (
                <Section kicker="Ranking" title="Top 20 colaboradores · horas perdidas no período" testId="abs-ranking">
                  <div className="overflow-x-auto">
                    <table className="w-full text-[12.5px]">
                      <thead>
                        <tr className="bg-slate-50 border-b border-border">
                          <th className="text-left px-3 py-2">#</th>
                          <th className="text-left px-3 py-2">Matrícula</th>
                          <th className="text-left px-3 py-2">Nome</th>
                          <th className="text-right px-3 py-2">Ocorrências</th>
                          <th className="text-right px-3 py-2">Horas perdidas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {kpis.ranking_colaboradores.map((r, i) => (
                          <tr key={`${r.employee_id}-${i}`} className="border-b border-border last:border-0">
                            <td className="px-3 py-2 text-muted-foreground">{i + 1}</td>
                            <td className="px-3 py-2 font-mono text-[11.5px]">{r.matricula}</td>
                            <td className="px-3 py-2">{r.nome}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{r.ocorrencias}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-semibold">{r.horas_perdidas}h</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Section>
              )}
            </>
          ) : null}
        </TabsContent>

        {/* ---- Registros ---- */}
        <TabsContent value="registros" className="space-y-3 mt-4">
          <div className="flex flex-wrap gap-2 items-end bg-white border border-border rounded-lg p-3">
            <div className="flex-1 min-w-[160px]">
              <Label className="text-[10px] uppercase tracking-wider">Matrícula</Label>
              <Input value={filters.matricula} onChange={(e) => setFilters({ ...filters, matricula: e.target.value })} placeholder="Buscar..." data-testid="abs-list-mat" />
            </div>
            <div className="w-[180px]">
              <Label className="text-[10px] uppercase tracking-wider">Tipo</Label>
              <Select value={filters.tipo} onValueChange={(v) => setFilters({ ...filters, tipo: v })}>
                <SelectTrigger data-testid="abs-list-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {Object.entries(TIPO_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[140px]">
              <Label className="text-[10px] uppercase tracking-wider">Origem</Label>
              <Select value={filters.origem} onValueChange={(v) => setFilters({ ...filters, origem: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="importacao">Importação</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-[160px]">
              <Label className="text-[10px] uppercase tracking-wider">De</Label>
              <DateInputBR value={filters.date_from} onChange={(v) => setFilters({ ...filters, date_from: v })} />
            </div>
            <div className="w-[160px]">
              <Label className="text-[10px] uppercase tracking-wider">Até</Label>
              <DateInputBR value={filters.date_to} onChange={(v) => setFilters({ ...filters, date_to: v })} />
            </div>
            <Button onClick={loadList} className="h-10 bg-[#234935] hover:bg-[#2c5c43]" data-testid="abs-list-apply">
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Aplicar
            </Button>
            <Button variant="outline" onClick={exportXlsx} className="h-10" data-testid="abs-xlsx-btn">
              <FileDown className="h-3.5 w-3.5 mr-1.5" /> Excel
            </Button>
          </div>

          {listLoading ? <Skeleton className="h-80 w-full" /> : (
            <div className="rounded-lg border border-border bg-white overflow-hidden">
              <div className="max-h-[560px] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-slate-50 z-10">
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Matrícula</TableHead>
                      <TableHead>Colaborador</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Horas / Dias</TableHead>
                      <TableHead>CID</TableHead>
                      <TableHead>Origem</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {list.items.length === 0 && (
                      <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Nenhum registro encontrado.</TableCell></TableRow>
                    )}
                    {list.items.map((a) => (
                      <TableRow key={a.id} data-testid={`abs-row-${a.id}`}>
                        <TableCell className="font-mono text-[12px]">{fmtDate(a.data_ausencia)}</TableCell>
                        <TableCell className="font-mono text-[12px]">{a.matricula}</TableCell>
                        <TableCell className="text-[12.5px]">{a.nome_colaborador || "—"}</TableCell>
                        <TableCell><Badge variant="outline">{TIPO_LABELS[a.tipo_ausencia] || a.tipo_ausencia}</Badge></TableCell>
                        <TableCell className="text-[12px] font-mono">{a.horas_ausentes}h / {a.dias_ausentes}d</TableCell>
                        <TableCell className="text-[12px]">{a.cid || "—"}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={a.origem === "importacao" ? "bg-sky-50 border-sky-300" : "bg-emerald-50 border-emerald-300"}>
                            {a.origem === "importacao" ? "Import" : "Manual"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {canEdit && (
                            <Button variant="ghost" size="icon" onClick={() => deleteAbsence(a)} data-testid={`abs-del-${a.id}`}>
                              <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="px-3 py-2 text-[11.5px] text-muted-foreground bg-slate-50 border-t">
                {list.total} registro(s)
              </div>
            </div>
          )}
        </TabsContent>

        {/* ---- Histórico ---- */}
        <TabsContent value="historico" className="space-y-3 mt-4">
          <div className="rounded-lg border border-border bg-white overflow-hidden">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead>Data/hora</TableHead>
                  <TableHead>Arquivo</TableHead>
                  <TableHead>Registros</TableHead>
                  <TableHead>Importado por</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {imports.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Nenhuma importação registrada.</TableCell></TableRow>
                )}
                {imports.map((imp) => (
                  <TableRow key={imp.id} data-testid={`imp-row-${imp.id}`}>
                    <TableCell className="font-mono text-[11.5px]">{fmtDateTime(imp.created_at)}</TableCell>
                    <TableCell className="text-[12.5px]">{imp.filename}</TableCell>
                    <TableCell className="font-mono text-[12px]">{imp.row_count}</TableCell>
                    <TableCell className="text-[12px]">{imp.imported_by_name || imp.imported_by_email}</TableCell>
                    <TableCell>
                      {imp.status === "reverted" ? (
                        <Badge variant="outline" className="bg-rose-50 border-rose-300 text-rose-700">Revertida</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-emerald-50 border-emerald-300 text-emerald-700">Ativa</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {canEdit && imp.status === "active" && (
                        <Button variant="ghost" size="sm" onClick={() => revertImport(imp)}
                                className="text-rose-600" data-testid={`imp-revert-${imp.id}`}>
                          <XCircle className="h-3.5 w-3.5 mr-1" /> Reverter
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* ==== Import Dialog ==== */}
      <Dialog open={importOpen} onOpenChange={(v) => { if (!v) resetImport(); setImportOpen(v); }}>
        <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="abs-import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-4 w-4" /> Importar Absenteísmo
            </DialogTitle>
            <DialogDescription>
              Passo {importStep} de 3 — {importStep === 1 ? "selecione arquivo Excel (.xlsx) ou CSV" : importStep === 2 ? "valide e confirme" : "conclusão"}
            </DialogDescription>
          </DialogHeader>

          {importStep === 1 && (
            <div className="space-y-3">
              <div className="rounded-md bg-[#FBF8F0] border border-[#E8D9A9] p-3 text-[12.5px] text-[#5F4B1F] flex items-start gap-2">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  Antes de importar, recomenda-se baixar o <b>modelo Excel</b> acima para garantir os cabeçalhos corretos.
                  A matrícula do colaborador deve existir no sistema — linhas com matrícula inválida serão marcadas como erro.
                </div>
              </div>
              <Label className="text-[11px] uppercase tracking-wider">Arquivo (.xlsx ou .csv)</Label>
              <Input ref={fileInputRef} type="file" accept=".xlsx,.csv" onChange={(e) => setImportFile(e.target.files?.[0] || null)} data-testid="abs-import-file" />
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => { resetImport(); setImportOpen(false); }}>Cancelar</Button>
                <Button onClick={doPreview} disabled={!importFile || importBusy} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="abs-preview-btn">
                  {importBusy ? "Lendo..." : "Analisar"}
                </Button>
              </div>
            </div>
          )}

          {importStep === 2 && importPreview && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-md border border-border bg-white p-3">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total no arquivo</div>
                  <div className="font-display text-2xl font-semibold">{importPreview.total_rows}</div>
                </div>
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700">Válidos</div>
                  <div className="font-display text-2xl font-semibold text-emerald-700">{importPreview.valid_count}</div>
                </div>
                <div className="rounded-md border border-rose-200 bg-rose-50 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-rose-700">Com erro</div>
                  <div className="font-display text-2xl font-semibold text-rose-700">{importPreview.invalid_count}</div>
                </div>
              </div>

              {importPreview.invalid.length > 0 && (
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-rose-700">Linhas rejeitadas (não serão importadas)</Label>
                  <div className="max-h-[180px] overflow-y-auto border border-rose-200 rounded-md mt-1">
                    <Table>
                      <TableHeader className="sticky top-0 bg-rose-50">
                        <TableRow>
                          <TableHead className="text-[11px]">#</TableHead>
                          <TableHead className="text-[11px]">Matrícula</TableHead>
                          <TableHead className="text-[11px]">Data</TableHead>
                          <TableHead className="text-[11px]">Erros</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {importPreview.invalid.map((r, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-[11.5px] font-mono">{r.row_index}</TableCell>
                            <TableCell className="text-[11.5px] font-mono">{r.matricula || "—"}</TableCell>
                            <TableCell className="text-[11.5px] font-mono">{r.data_ausencia ? fmtDate(r.data_ausencia) : "—"}</TableCell>
                            <TableCell className="text-[11.5px] text-rose-700">{(r.errors || []).join(" · ")}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {importPreview.valid.length > 0 && (
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-emerald-700">Linhas válidas (serão importadas)</Label>
                  <div className="max-h-[220px] overflow-y-auto border border-emerald-200 rounded-md mt-1">
                    <Table>
                      <TableHeader className="sticky top-0 bg-emerald-50">
                        <TableRow>
                          <TableHead className="text-[11px]">Matrícula</TableHead>
                          <TableHead className="text-[11px]">Colaborador</TableHead>
                          <TableHead className="text-[11px]">Data</TableHead>
                          <TableHead className="text-[11px]">Tipo</TableHead>
                          <TableHead className="text-[11px]">Horas/Dias</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {importPreview.valid.slice(0, 50).map((r, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-[11.5px] font-mono">{r.matricula}</TableCell>
                            <TableCell className="text-[11.5px]">{r.nome_colaborador || "—"}</TableCell>
                            <TableCell className="text-[11.5px] font-mono">{fmtDate(r.data_ausencia)}</TableCell>
                            <TableCell className="text-[11.5px]">{TIPO_LABELS[r.tipo_ausencia] || r.tipo_ausencia}</TableCell>
                            <TableCell className="text-[11.5px] font-mono">{r.horas_ausentes}h / {r.dias_ausentes}d</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {importPreview.valid.length > 50 && (
                    <div className="text-[11px] text-muted-foreground mt-1 text-center">… mais {importPreview.valid.length - 50} linhas</div>
                  )}
                </div>
              )}

              <div className="flex justify-between gap-2 pt-1">
                <Button variant="outline" onClick={() => setImportStep(1)}>Voltar</Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { resetImport(); setImportOpen(false); }}>Cancelar</Button>
                  <Button onClick={doCommit} disabled={importBusy || importPreview.valid_count === 0}
                          className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="abs-commit-btn">
                    {importBusy ? "Importando..." : `Confirmar e importar ${importPreview.valid_count} registro(s)`}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {importStep === 3 && (
            <div className="py-6 text-center space-y-3">
              <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
              <div className="font-display text-xl font-semibold">Importação concluída!</div>
              <div className="text-[13px] text-muted-foreground">
                Os registros foram adicionados ao absenteísmo e os indicadores já foram recalculados.
              </div>
              <Button onClick={() => { resetImport(); setImportOpen(false); setTab("historico"); }} className="bg-[#234935]">
                <History className="h-3.5 w-3.5 mr-1.5" /> Ver histórico
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ==== Manual Dialog ==== */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-xl" data-testid="abs-manual-dialog">
          <DialogHeader>
            <DialogTitle>Lançamento manual de ausência</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase">Matrícula *</Label>
              <Input value={manualForm.matricula} onChange={(e) => setManualForm({ ...manualForm, matricula: e.target.value })} data-testid="man-mat" />
            </div>
            <div>
              <Label className="text-[11px] uppercase">Data *</Label>
              <DateInputBR value={manualForm.data_ausencia} onChange={(v) => setManualForm({ ...manualForm, data_ausencia: v })} testId="man-data" />
            </div>
            <div className="col-span-2">
              <Label className="text-[11px] uppercase">Tipo *</Label>
              <Select value={manualForm.tipo_ausencia} onValueChange={(v) => setManualForm({ ...manualForm, tipo_ausencia: v })}>
                <SelectTrigger data-testid="man-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TIPO_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase">Horas</Label>
              <Input type="number" step="0.5" value={manualForm.horas_ausentes} onChange={(e) => setManualForm({ ...manualForm, horas_ausentes: +e.target.value })} />
            </div>
            <div>
              <Label className="text-[11px] uppercase">Dias</Label>
              <Input type="number" step="0.5" value={manualForm.dias_ausentes} onChange={(e) => setManualForm({ ...manualForm, dias_ausentes: +e.target.value })} />
            </div>
            <div>
              <Label className="text-[11px] uppercase">Justificativa</Label>
              <Select value={manualForm.justificativa} onValueChange={(v) => setManualForm({ ...manualForm, justificativa: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="justificada">Justificada</SelectItem>
                  <SelectItem value="injustificada">Injustificada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase">CID</Label>
              <Input value={manualForm.cid} onChange={(e) => setManualForm({ ...manualForm, cid: e.target.value })} placeholder="Ex: J11" />
            </div>
            <div className="col-span-2">
              <Label className="text-[11px] uppercase">Motivo</Label>
              <Input value={manualForm.motivo_ausencia} onChange={(e) => setManualForm({ ...manualForm, motivo_ausencia: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label className="text-[11px] uppercase">Observação</Label>
              <Textarea rows={2} value={manualForm.observacao} onChange={(e) => setManualForm({ ...manualForm, observacao: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setManualOpen(false)}>Cancelar</Button>
            <Button onClick={saveManual} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="man-save">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ============================================================
// Advanced Panel — new indicators using /absenteismo-indicators
// ============================================================
function AdvancedPanel({ companyParam }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("30d");
  const [criterio, setCriterio] = useState("ambos");
  const [cfg, setCfg] = useState(null);
  const [cfgOpen, setCfgOpen] = useState(false);
  const { hasRole } = useAuth();
  const canEditCfg = hasRole("master", "rh_admin");

  const computeRange = () => {
    const to = new Date();
    const from = new Date();
    if (period === "30d") from.setDate(from.getDate() - 30);
    else if (period === "90d") from.setDate(from.getDate() - 90);
    else if (period === "ytd") from.setMonth(0, 1);
    else if (period === "12m") from.setMonth(from.getMonth() - 12);
    return { date_from: from.toISOString().slice(0, 10), date_to: to.toISOString().slice(0, 10) };
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (companyParam) params.company_id = companyParam;
      const r = computeRange();
      params.date_from = r.date_from;
      params.date_to = r.date_to;
      const { data: d } = await api.get("/absenteismo-indicators/dashboard", { params });
      setData(d);
      setCfg(d.config);
      setCriterio(d.config?.criterio_calculo || "ambos");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [companyParam, period]);

  const saveCfg = async () => {
    try {
      await api.put("/absenteismo-indicators/config", cfg);
      toast.success("Configurações salvas");
      setCfgOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (loading) return <GridSkeleton />;
  if (!data) return null;

  const k = data.kpis || {};
  const r = data.rankings || {};
  const alertaTone = k.alerta === "vermelho" ? "rose"
    : k.alerta === "amarelo" ? "ochre" : "emerald";
  const currency = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  return (
    <div className="space-y-4" data-testid="abs-advanced-panel">
      {/* Filters + criterio */}
      <div className="flex items-center gap-2 flex-wrap bg-white border border-border rounded-lg p-3">
        <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Período</Label>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[160px]" data-testid="abs-period"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="30d">Últimos 30 dias</SelectItem>
            <SelectItem value="90d">Últimos 90 dias</SelectItem>
            <SelectItem value="ytd">Ano atual</SelectItem>
            <SelectItem value="12m">Últimos 12 meses</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-2 px-2 py-1 rounded bg-slate-100 text-[11px] font-medium text-slate-700">
          Cálculo: <span className="font-semibold text-slate-900 capitalize">{criterio}</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {canEditCfg && (
            <Button size="sm" variant="outline" onClick={() => setCfgOpen(true)} data-testid="abs-config-btn">
              <Settings2 className="h-3.5 w-3.5 mr-1" /> Configurações
            </Button>
          )}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="% Absenteísmo" value={`${k.percentual_absenteismo}%`}
                 sub={`Alerta: ${k.alerta} (amarelo ≥ ${k.alerta_amarelo_pct}% · vermelho ≥ ${k.alerta_vermelho_pct}%)`}
                 icon={Clock} accent={alertaTone} />
        <KpiCard label="Ocorrências" value={k.total_ocorrencias} sub={`${k.colaboradores_impactados} colaboradores impactados`} icon={AlertCircle} accent="ochre" />
        <KpiCard label="Dias perdidos" value={k.dias_perdidos} sub={`Horas perdidas: ${k.horas_perdidas}h`} icon={AlertCircle} accent="rose" />
        <KpiCard label="Custo estimado" value={currency(k.custo_estimado)} sub="Salário ÷ 220h ou custo/hora manual" icon={Info} accent="slate" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <KpiCard label="Setor crítico" value={k.setor_critico || "—"} accent="rose" />
        <KpiCard label="Situação recorrente" value={(k.situacao_recorrente || "—").replace(/_/g, " ")} accent="ochre" />
        <KpiCard label="Colaboradores ativos no período" value={k.total_ativos_periodo} sub={`Horas previstas: ${k.horas_previstas_periodo}h`} accent="emerald" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Section kicker="Ranking" title="Top 10 setores (horas perdidas)">
          <div className="h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={(r.setores || []).slice(0, 10)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DD" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="nome" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={150} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="horas" fill="#234935" name="Horas perdidas" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>

        <Section kicker="Evolução" title="Horas perdidas por mês">
          <div className="h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.evolucao_mensal || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DD" />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="horas" fill="#C5A059" name="Horas" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Section kicker="Ranking" title="Top 10 colaboradores">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Matrícula</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>Setor</TableHead>
                <TableHead className="text-right">Ocorr.</TableHead>
                <TableHead className="text-right">Horas</TableHead>
                <TableHead className="text-right">Custo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(r.colaboradores || []).slice(0, 10).map((c, i) => (
                <TableRow key={i}>
                  <TableCell className="font-mono text-[11.5px]">{c.matricula || "—"}</TableCell>
                  <TableCell className="text-[12.5px]">{c.nome || "—"}</TableCell>
                  <TableCell className="text-[12.5px] text-muted-foreground">{c.setor}</TableCell>
                  <TableCell className="text-right">{c.ocorrencias}</TableCell>
                  <TableCell className="text-right">{c.horas.toFixed(1)}</TableCell>
                  <TableCell className="text-right font-mono text-[11.5px]">{currency(c.custo)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>

        <Section kicker="Categorias" title="Distribuição por situação">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Situação</TableHead>
                <TableHead className="text-right">Ocorr.</TableHead>
                <TableHead className="text-right">Dias</TableHead>
                <TableHead className="text-right">Horas</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(r.categorias || []).slice(0, 12).map((c, i) => (
                <TableRow key={i}>
                  <TableCell className="text-[12.5px] capitalize">{(c.nome || "").replace(/_/g, " ")}</TableCell>
                  <TableCell className="text-right">{c.ocorrencias}</TableCell>
                  <TableCell className="text-right">{c.dias.toFixed(1)}</TableCell>
                  <TableCell className="text-right">{c.horas.toFixed(1)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>
      </div>

      {/* Config dialog */}
      {cfg && (
        <Dialog open={cfgOpen} onOpenChange={setCfgOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle className="font-display">Configurações do Absenteísmo</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-[11px] uppercase">Critério de cálculo</Label>
                <Select value={cfg.criterio_calculo} onValueChange={(v) => setCfg({ ...cfg, criterio_calculo: v })}>
                  <SelectTrigger data-testid="cfg-criterio"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dias">Por dias</SelectItem>
                    <SelectItem value="horas">Por horas</SelectItem>
                    <SelectItem value="ambos">Ambos (dias e horas)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase">Jornada diária (h)</Label>
                <Input type="number" step="0.1" value={cfg.jornada_diaria_horas}
                       onChange={(e) => setCfg({ ...cfg, jornada_diaria_horas: Number(e.target.value) })} />
              </div>
              <div>
                <Label className="text-[11px] uppercase">Jornada mensal (h)</Label>
                <Input type="number" step="0.1" value={cfg.jornada_mensal_horas}
                       onChange={(e) => setCfg({ ...cfg, jornada_mensal_horas: Number(e.target.value) })} />
              </div>
              <div>
                <Label className="text-[11px] uppercase">Dias úteis/mês</Label>
                <Input type="number" value={cfg.dias_uteis_mes}
                       onChange={(e) => setCfg({ ...cfg, dias_uteis_mes: Number(e.target.value) })} />
              </div>
              <div>
                <Label className="text-[11px] uppercase">Custo/hora</Label>
                <Select value={cfg.custo_hora_calculo} onValueChange={(v) => setCfg({ ...cfg, custo_hora_calculo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Automático (salário/220h)</SelectItem>
                    <SelectItem value="manual">Manual (campo custo_hora)</SelectItem>
                    <SelectItem value="hybrid">Híbrido (manual sobrescreve)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase">Alerta amarelo (%)</Label>
                <Input type="number" step="0.1" value={cfg.alerta_amarelo_pct}
                       data-testid="cfg-amarelo"
                       onChange={(e) => setCfg({ ...cfg, alerta_amarelo_pct: Number(e.target.value) })} />
              </div>
              <div>
                <Label className="text-[11px] uppercase">Alerta vermelho (%)</Label>
                <Input type="number" step="0.1" value={cfg.alerta_vermelho_pct}
                       data-testid="cfg-vermelho"
                       onChange={(e) => setCfg({ ...cfg, alerta_vermelho_pct: Number(e.target.value) })} />
              </div>
              <div className="col-span-2 flex items-center justify-between p-3 bg-slate-50 rounded-md">
                <div>
                  <div className="text-[12.5px] font-medium">Considerar escala do colaborador</div>
                  <div className="text-[11px] text-muted-foreground">Usar jornada da escala cadastrada (fallback: jornada padrão acima)</div>
                </div>
                <Switch checked={!!cfg.considera_escala}
                        onCheckedChange={(v) => setCfg({ ...cfg, considera_escala: v })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCfgOpen(false)}>Cancelar</Button>
              <Button onClick={saveCfg} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="cfg-save">
                Salvar configurações
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
